import { useEffect, useState } from 'react';
import { Search, ChevronRight, Crown, Users, ArrowUpRight } from 'lucide-react';

/**
 * Animated replica of the real WapStat census flow (English/LTR — WapStat's UI
 * is English; only its PDFs are Thaana). A census round in progress: households
 * are visited one by one, each flips from "To visit" to its recorded state
 * (current / a birth / a move), and the round's change tally ticks up as the
 * living register updates. Steps on a timer and loops.
 */

type Status = 'Current' | 'Updated' | 'Moved';

const HOUSEHOLDS: { house: string; ward: string; head: string; members: number; status: Status; note: string }[] = [
  { house: 'Finifenmaage', ward: 'Ward 2', head: 'Ibrahim Waheed', members: 5, status: 'Updated', note: '+1 birth · Aug' },
  { house: 'Roazuvaage', ward: 'Ward 1', head: 'Aishath Nazima', members: 4, status: 'Current', note: 'no change' },
  { house: 'Nooraaneege', ward: 'Ward 3', head: 'Mohamed Rasheed', members: 6, status: 'Current', note: 'no change' },
  { house: 'Alivaage', ward: 'Ward 2', head: 'Fathimath Shifa', members: 3, status: 'Moved', note: 'moved to Malé' },
  { house: 'Kanamanaage', ward: 'Ward 3', head: 'Ali Zahir', members: 7, status: 'Updated', note: 'address change' },
];

const STATUS_STYLE: Record<Status, { bg: string; fg: string }> = {
  Current: { bg: 'hsl(var(--success) / 0.14)', fg: 'hsl(var(--success))' },
  Updated: { bg: 'hsl(var(--accent-cream))', fg: 'hsl(var(--accent-ink))' },
  Moved: { bg: 'hsl(var(--muted))', fg: 'hsl(var(--muted-foreground))' },
};

// One household visited per step; the last state holds, then loops.
const STEP_MS = [1150, 950, 950, 950, 950];
const LAST = HOUSEHOLDS.length; // 5

const PALETTE = {
  '--background': '36 24% 96.5%',
  '--foreground': '24 16% 10%',
  '--card': '38 30% 99%',
  '--primary': '178 84% 28%',
  '--muted': '33 18% 92%',
  '--muted-foreground': '24 8% 38%',
  '--border': '30 12% 84%',
  '--input': '30 12% 84%',
  '--destructive': '4 65% 45%',
  '--accent-cream': '38 60% 86%',
  '--accent-ink': '24 28% 14%',
  '--success': '156 50% 32%',
  fontFamily: '"DM Sans", system-ui, sans-serif',
} as React.CSSProperties;

export function WapStatScreen(): JSX.Element {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setStep(LAST);
      return;
    }
    const ms = step >= LAST ? 2800 : STEP_MS[step] ?? 1000;
    const t = setTimeout(() => setStep((s) => (s >= LAST ? 0 : s + 1)), ms);
    return () => clearTimeout(t);
  }, [step]);

  const visited = Math.min(HOUSEHOLDS.length, step);
  const done = visited >= HOUSEHOLDS.length;
  const changesFound = HOUSEHOLDS.slice(0, visited).filter((h) => h.status !== 'Current').length;
  const changes = 24 + changesFound; // round tally ticks up to 27

  return (
    <div dir="ltr" className="app-screen h-full w-full overflow-hidden bg-background text-foreground" style={PALETTE}>
      <div className="flex h-full flex-col">
        {/* header */}
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-bold leading-tight tracking-tight">Household Register</span>
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: 'hsl(var(--success) / 0.14)', color: 'hsl(var(--success))' }}>
                <span className="size-1.5 rounded-full" style={{ background: 'hsl(var(--success))' }} /> Current cycle
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Hithadhoo · Census 2026 · Round 2</p>
          </div>
          <div className="hidden items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[11px] text-muted-foreground sm:flex" style={{ background: 'hsl(var(--muted) / 0.5)' }}>
            {done ? 'Round complete' : (
              <>
                Visiting <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>{visited}/{HOUSEHOLDS.length}</span>
              </>
            )}
          </div>
        </div>

        {/* stats */}
        <div className="grid grid-cols-3 gap-px border-b border-border" style={{ background: 'hsl(var(--border))' }}>
          <div className="bg-card px-5 py-3">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Users size={12} /> Residents
            </div>
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span className="text-2xl font-bold leading-none tracking-tight">1,240</span>
              {done && (
                <span key="delta" className="ws-pop inline-flex items-center gap-0.5 text-[11px] font-semibold" style={{ color: 'hsl(var(--success))' }}>
                  <ArrowUpRight size={12} /> +18
                </span>
              )}
            </div>
          </div>
          <div className="bg-card px-5 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Households</div>
            <div className="mt-0.5 text-2xl font-bold leading-none tracking-tight">312</div>
          </div>
          <div className="bg-card px-5 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Changes</div>
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span className="text-2xl font-bold leading-none tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>{changes}</span>
              <span className="text-[11px] text-muted-foreground">this round</span>
            </div>
          </div>
        </div>

        {/* toolbar */}
        <div className="flex items-center gap-2 px-5 py-2.5">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <div className="flex h-8 items-center rounded-lg border border-input pl-8 pr-3 text-[12px] text-muted-foreground" style={{ background: 'hsl(var(--card))' }}>
              Search households or residents…
            </div>
          </div>
          <span className="rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground" style={{ background: 'hsl(var(--card))' }}>
            All wards
          </span>
        </div>

        {/* household list */}
        <div className="min-h-0 flex-1 overflow-hidden px-5 pb-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <ul className="divide-y divide-border">
              {HOUSEHOLDS.map((h, i) => {
                const seen = i < visited;
                const s = STATUS_STYLE[h.status];
                return (
                  <li key={h.house} className="flex items-center gap-3 px-3.5 py-2.5" style={!seen ? { opacity: 0.55 } : undefined}>
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg text-[13px] font-bold" style={{ background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' }}>
                      {h.house.charAt(0)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-[13px] font-semibold leading-snug text-foreground">{h.house}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{h.ward}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Crown size={11} style={{ color: 'hsl(var(--accent-ink))' }} />
                        <span className="truncate">{h.head}</span>
                        <span>·</span>
                        <span className="shrink-0">{h.members} residents</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {seen ? (
                        <>
                          <span key="chip" className="ws-pop inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: s.bg, color: s.fg }}>
                            {h.status}
                          </span>
                          <span className="text-[10px] text-muted-foreground">{h.note}</span>
                        </>
                      ) : (
                        <span className="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>
                          To visit
                        </span>
                      )}
                    </div>
                    <ChevronRight size={15} className="shrink-0 text-muted-foreground" />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
